import {Formik} from "formik";
import {Container} from "./styled";
import {Check} from "react-feather";
import {buildSelectOptions} from "utils";
import {useEffect, useState} from "react";
import {useTheme} from "styled-components";
import {useAppDispatch, useAppSelector} from "hooks";
import {CreditModel, LoanFinancePaymentModel} from "interfaces";
import {reportPaymentSchema, initialValues} from "./form.schema";
import {getBanks, reportPayment} from "global/slices/payments/thunks";
import {Column, Button, Input, Select, MultimediaManager} from "components";

interface FormProps {
  credit: CreditModel;
}

export const Form: React.FC<FormProps> = ({credit}) => {
  const dispatch = useAppDispatch();
  const {banks} = useAppSelector("payments");
  const [payment, setPayment] = useState<LoanFinancePaymentModel>(
    {} as LoanFinancePaymentModel,
  );
  const {disabledButtonTextColor, black} = useTheme();
  const {finanzas, cedula, telefono} = credit;
  const {payment_reports} = finanzas;
  const pendingPayments = payment_reports.filter(
    (payment) => payment.approved === 0,
  );

  useEffect(() => {
    dispatch(getBanks());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <Formik
        enableReinitialize
        validationSchema={reportPaymentSchema}
        initialValues={{
          ...initialValues,
          id: String(payment.id),
          dni: cedula ?? "",
          phone: telefono ?? "",
          finanza_id: String(finanzas["id"]),
          reported_amount: payment.amount,
          formulario_id: String(finanzas["formulario_id"]),
        }}
        onSubmit={(payload) => {
          // @ts-expect-error
          dispatch(reportPayment(payload));
        }}
      >
        {({
          dirty,
          errors,
          values,
          isValid,
          touched,
          handleBlur,
          handleSubmit,
          handleChange,
        }) => {
          const isDisabled = !(dirty && isValid) || !payment.id;

          return (
            <Column gap={10} width="100%">
              <Select
                name="id"
                value={values["id"]}
                touched={touched["id"]}
                onBlur={handleBlur("id")}
                label="Pagos no reportados"
                helperErrorText={errors["id"]}
                onChange={({target}) => {
                  const {value} = target;
                  const temp = pendingPayments.find(
                    (payment) => String(payment["id"]) === value,
                  );
                  setPayment(temp ?? ({} as LoanFinancePaymentModel));
                }}
                options={[
                  {label: "Selecciona un pago", value: ""},
                  ...pendingPayments.map((payment) => ({
                    value: String(payment["id"]),
                    label: `ID: ${payment["id"]} | Fecha: ${payment["date"]}, monto: ${payment["amount"]}`,
                  })),
                ]}
              />
              <Input
                type="number"
                placeholder="50"
                inputMode="numeric"
                value={values["reported_amount"]}
                touched={touched["reported_amount"]}
                onBlur={handleBlur("reported_amount")}
                label="Monto (transfiere el monto exacto)"
                helperErrorText={errors["reported_amount"]}
                onChange={handleChange("reported_amount")}
              />
              <Input
                label="Cédula"
                inputMode="numeric"
                value={values["dni"]}
                touched={touched["dni"]}
                onBlur={handleBlur("dni")}
                helperErrorText={errors["dni"]}
                onChange={handleChange("dni")}
                placeholder="12333444"
              />
              <Input
                label="Teléfono"
                inputMode="numeric"
                value={values["phone"]}
                touched={touched["phone"]}
                onBlur={handleBlur("phone")}
                helperErrorText={errors["phone"]}
                onChange={handleChange("phone")}
                placeholder="04121112233"
              />
              <Input
                inputMode="numeric"
                value={values["reference"]}
                touched={touched["reference"]}
                onBlur={handleBlur("reference")}
                helperErrorText={errors["reference"]}
                label="Número/Código de referencia"
                onChange={handleChange("reference")}
                placeholder="Nro. depósito / transferencia"
              />
              <Select
                value={values["bank_id"]}
                touched={touched["bank_id"]}
                label="Desde donde envió el dinero"
                onBlur={handleBlur("bank_id")}
                onChange={handleChange("bank_id")}
                helperErrorText={errors["bank_id"]}
                options={buildSelectOptions(
                  banks,
                  "Selecciona el banco emisor",
                  {keyValue: "id", keyLabel: "name"},
                )}
                onSelect={(value) => {
                  handleChange({
                    target: {
                      name: "bank_id",
                      value: value,
                    },
                  });
                }}
              />
              <Input
                type="date"
                label="Fecha"
                placeholder="Fecha de pago"
                value={values["reported_date"]}
                touched={touched["reported_date"]}
                onBlur={handleBlur("reported_date")}
                helperErrorText={errors["reported_date"]}
                onChange={handleChange("reported_date")}
              />
              <MultimediaManager
                multiple={false}
                extension="image"
                helperErrorText={errors["capture"]}
                media={values["capture"] ? [values["capture"] as string] : []}
                onNewMedia={(files) => {
                  handleChange({
                    target: {name: "capture", value: files[0] ?? ""},
                  });
                }}
                onRemove={() => {
                  handleChange({
                    target: {name: "capture", value: ""},
                  });
                }}
              />
              <Column width="100%" alignItems="center" marginTop={10}>
                <Button
                  label="Reportar pago"
                  disabled={isDisabled}
                  style={{width: "100%"}}
                  loaderId="report-payment"
                  onClick={() => handleSubmit()}
                  rightIcon={
                    <Check
                      color={isDisabled ? disabledButtonTextColor : black}
                    />
                  }
                />
              </Column>
            </Column>
          );
        }}
      </Formik>
    </Container>
  );
};
